import { createContext, ReactElement, useContext, useId, useMemo } from "react";
import { Popover } from "../../../Popover";
import { Icon } from "../../../Icon";
import MoreHoriz from "@servicetitan/hammer-icon/mdi/round/more_horiz.svg";
import MoreVert from "@servicetitan/hammer-icon/mdi/round//more_vert.svg";
import {
  ToolbarButton,
  ToolbarButtonProps,
} from "../ToolbarButton/ToolbarButton";
import {
  ToolbarButtonToggle,
  ToolbarButtonToggleProps,
} from "../ToolbarButtonToggle/ToolbarButtonToggle";
import {
  ToolbarButtonLink,
  ToolbarButtonLinkProps,
} from "../ToolbarButtonLink/ToolbarButtonLink";
import {
  ToolbarSelect,
  ToolbarSelectProps,
} from "../ToolbarSelect/ToolbarSelect";
import { ToolbarItemProps } from "../../sharedTypes";
import { ToolbarOverflowContext } from "../../Toolbar";

import cx from "classnames";
import styles from "../../Toolbar.module.scss";
import { handleMenuKeyboardNavigation } from "../../utils/accessibility";

/**
 * Context for denoting if the current item is rendered in the overflow menu
 */
type OverflowItemContextType = {
  /**
   * Whether the current item is rendered inside the overflow menu
   */
  isInOverflowMenu: boolean;
};

/**
 * Context for managing overflow menu state
 */
export const OverflowItemContext = createContext<OverflowItemContextType>({
  isInOverflowMenu: false,
});

/**
 * Hook for accessing overflow menu context
 * @returns OverflowItemContextType - The overflow menu context
 */
export const useOverFlowItemContext = () => useContext(OverflowItemContext);

/**
 * Component to render appropriate toolbar items in the overflow menu
 * @param itemType - Type of toolbar item to render
 * @param itemProps - Props for the toolbar item
 * @returns ReactElement - The rendered toolbar item
 */
const OverflowItem = ({ itemType, itemProps }: ToolbarItemProps) => {
  // Based on the item type, use the corresponding toolbar component
  switch (itemType) {
    case "button":
      return <ToolbarButton {...(itemProps as ToolbarButtonProps)} />;

    case "buttonToggle":
      return (
        <ToolbarButtonToggle {...(itemProps as ToolbarButtonToggleProps)} />
      );

    case "buttonLink":
      return <ToolbarButtonLink {...(itemProps as ToolbarButtonLinkProps)} />;

    case "select":
      return <ToolbarSelect {...(itemProps as ToolbarSelectProps)} />;

    default:
      return null;
  }
};

/**
 * Props for the ToolbarOverflowMenu component
 */
export type ToolbarOverflowMenuProps = {
  /**
   * Additional items to display in the overflow menu
   */
  additionalItems?: ReactElement[];
  /**
   * Direction of the parent toolbar
   * @default horizontal
   */
  direction?: "vertical" | "horizontal";
};

/**
 * ToolbarOverflowMenu component displays toolbar items that don't fit in the main toolbar.
 *
 * Features:
 * - Automatic rendering when items overflow in collapse mode
 * - Maintains consistent item ordering with the main toolbar
 * - Preserves item state between toolbar and overflow menu
 * - Provides accessible navigation with keyboard and screen readers
 * - Adapts to both horizontal and vertical toolbar layouts
 * - Context-aware rendering for child components
 * - Proper ARIA menu semantics
 * - Keyboard navigation support
 */
export const ToolbarOverflowMenu = ({
  additionalItems,
  direction,
}: ToolbarOverflowMenuProps) => {
  // Gives us the array of items that need to appear
  const { overflowItems, orderedIds } = useContext(ToolbarOverflowContext);
  const menuTriggerId = useId();

  // Sort overflow items to match their original order in the toolbar
  const orderedItems = useMemo(() => {
    const allElements = orderedIds?.map((id) => {
      return overflowItems.find((item) => item.id === id);
    });

    return allElements?.filter((item) => item !== undefined) || [];
  }, [orderedIds, overflowItems]);

  const popoverButtonClassNames = cx(
    styles["toolbar-button-item"],
    styles["toolbar-overflow-trigger"],
  );

  const overflowIcon = direction === "horizontal" ? MoreHoriz : MoreVert;

  return (
    <Popover noPadding>
      <Popover.Button
        size="small"
        appearance="ghost"
        id={menuTriggerId}
        className={popoverButtonClassNames}
        data-anv="toolbar-overflow-trigger"
        aria-label="Additional toolbar options"
      >
        <Icon svg={overflowIcon} size="small" />
      </Popover.Button>
      <Popover.Content>
        <OverflowItemContext.Provider value={{ isInOverflowMenu: true }}>
          <ul
            role="menu"
            className={styles["toolbar-overflow-content"]}
            data-anv="toolbar-overflow-menu"
            aria-labelledby={menuTriggerId}
            onKeyDown={handleMenuKeyboardNavigation}
          >
            {orderedItems.map((item) => {
              const { itemType, itemProps, id } = item;
              return (
                <li
                  key={`${itemType}-${id}`}
                  role="menuitem"
                  data-anv="toolbar-overflow-item"
                >
                  <OverflowItem
                    itemType={itemType}
                    itemProps={itemProps}
                    id={id}
                  />
                </li>
              );
            })}

            {additionalItems?.map((item, index) => (
              <li
                key={`additional-item-${index}`}
                role="menuitem"
                data-anv="toolbar-overflow-item"
              >
                {item}
              </li>
            ))}
          </ul>
        </OverflowItemContext.Provider>
      </Popover.Content>
    </Popover>
  );
};
